/**
 * PDP colour / size-sibling link helpers.
 * Navigation uses normal same-origin product links; cross-document view
 * transitions are enabled via @view-transition in product.liquid CSS.
 */

const NAV_LINK_SELECTOR = '[data-product-colour-nav]';
const FAMILY_CACHE_PREFIX = 'lyle-colour-family:';

let bound = false;

export function setColourFamilyCache(familyTag, products) {
  if (!familyTag || !products?.length) return;

  try {
    sessionStorage.setItem(
      `${FAMILY_CACHE_PREFIX}${familyTag}`,
      JSON.stringify(products),
    );
  } catch {
    // ignore quota / private mode
  }
}

export function getColourFamilyCache(familyTag) {
  if (!familyTag) return null;

  try {
    const raw = sessionStorage.getItem(`${FAMILY_CACHE_PREFIX}${familyTag}`);
    if (!raw) return null;

    const products = JSON.parse(raw);
    return Array.isArray(products) && products.length ? products : null;
  } catch {
    return null;
  }
}

export function bindProductColourNav() {
  if (bound) return;
  bound = true;

  document.addEventListener('click', onClick);
  document.addEventListener('pointerover', onPointerOver);
}

function onClick(e) {
  const link = e.target.closest(NAV_LINK_SELECTOR);
  if (!link) return;

  if (e.defaultPrevented) return;
  if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey || e.button !== 0) return;

  if (
    link.classList.contains('is-active') ||
    link.getAttribute('aria-current') === 'page' ||
    link.hasAttribute('disabled')
  ) {
    e.preventDefault();
    return;
  }

  if (link.hasAttribute('data-swatch')) {
    trackColourSuccess();
  }
}

function onPointerOver(e) {
  const link = e.target.closest(NAV_LINK_SELECTOR);
  if (!link?.href) return;

  if (
    link.classList.contains('is-active') ||
    link.getAttribute('aria-current') === 'page'
  ) {
    return;
  }

  if (!isSameOriginProductUrl(link.href)) return;

  prefetchImagesFromLink(link);
}

function prefetchImagesFromLink(link) {
  const raw = link.dataset.prefetchImages;
  if (!raw) return;

  try {
    const urls = JSON.parse(raw);
    if (!Array.isArray(urls)) return;

    for (const src of urls.slice(0, 3)) {
      if (!src) continue;
      const img = new Image();
      img.decoding = 'async';
      img.src = src;
    }
  } catch {
    // ignore malformed prefetch payloads
  }
}

function isSameOriginProductUrl(url) {
  try {
    const parsed = new URL(url, window.location.origin);
    if (parsed.origin !== window.location.origin) return false;
    return /\/products\//.test(parsed.pathname);
  } catch {
    return false;
  }
}

function trackColourSuccess() {
  if (typeof window.gtag === 'function') {
    window.gtag('event', 'pdp_colour_success');
    return;
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event: 'pdp_colour_success' });
}

bindProductColourNav();
